import { useState, useEffect, useCallback } from 'react';
import { db } from '../lib/firebase';
import { 
  collection, 
  getDocs, 
  query, 
  where,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  orderBy as firestoreOrderBy,
  limit as firestoreLimit,
  DocumentData,
  Transaction,
  writeBatch,
  QueryConstraint,
  runTransaction
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { 
  Inventory, 
  InventoryMovement, 
  InventoryMovementType, 
  CreateInventoryMovementData,
  InventoryQueryParams,
  InventoryReport
} from '../types/warehouse';

export const useInventory = () => {
  const [inventory, setInventory] = useState<Inventory[]>([]);
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const { hasAccess } = usePermissions();

  // Készletadatok betöltése
  const fetchInventory = useCallback(async (params?: InventoryQueryParams) => {
    // Ha nincs hozzáférés, ne töltsünk be adatokat
    if (!hasAccess('inventory')) {
      setLoading(false);
      return [];
    }

    setLoading(true);
    try {
      const inventoryRef = collection(db, 'inventory');
      const constraints: QueryConstraint[] = [];
      
      // Szűrési feltételek hozzáadása
      if (params?.productId) {
        constraints.push(where('productId', '==', params.productId));
      }
      
      if (params?.productCode) {
        constraints.push(where('productCode', '==', params.productCode));
      }
      
      if (params?.warehouseId) {
        constraints.push(where('warehouseId', '==', params.warehouseId));
      }
      
      if (params?.locationId) {
        constraints.push(where('locationId', '==', params.locationId));
      }
      
      const q = constraints.length > 0 
        ? query(inventoryRef, ...constraints)
        : query(inventoryRef);
        
      const snapshot = await getDocs(q);
      
      let inventoryItems = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      })) as Inventory[];
      
      // További szűrések, amelyeket Firestore-ban nem tudunk közvetlenül kezelni
      if (params?.belowMinimum) {
        inventoryItems = inventoryItems.filter(
          item => item.minQuantity !== undefined && item.quantity < item.minQuantity
        );
      }
      
      if (params?.aboveMaximum) {
        inventoryItems = inventoryItems.filter(
          item => item.maxQuantity !== undefined && item.quantity > item.maxQuantity
        );
      }
      
      if (params?.onlyAvailable) {
        inventoryItems = inventoryItems.filter(item => item.quantity > 0);
      }
      
      if (params?.searchTerm) {
        const term = params.searchTerm.toLowerCase();
        inventoryItems = inventoryItems.filter(item => 
          item.productName.toLowerCase().includes(term) || 
          item.productCode.toLowerCase().includes(term)
        );
      }
      
      setInventory(inventoryItems);
      setError(null);
      return inventoryItems;
    } catch (err) {
      console.error('Hiba a készletadatok betöltésekor:', err);
      setError('Nem sikerült betölteni a készletadatokat.');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Készletmozgások betöltése
  const fetchMovements = useCallback(async (params: {
    productId?: string;
    warehouseId?: string;
    fromDate?: Date;
    toDate?: Date;
    type?: InventoryMovementType;
    limit?: number;
    lastDoc?: DocumentData | null;
    pageSize?: number;
    sortOrder?: 'asc' | 'desc';
  } = {}) => {
    // Ha nincs hozzáférés, ne töltsünk be adatokat
    if (!hasAccess('inventory')) {
      setLoading(false);
      return {
        movements: [],
        lastDoc: null,
        totalCount: 0
      };
    }

    setLoading(true);
    try {
      const movementsRef = collection(db, 'inventoryMovements');
      const constraints: QueryConstraint[] = [];
      
      if (params.productId) {
        constraints.push(where('productId', '==', params.productId));
      }
      
      if (params.warehouseId) {
        constraints.push(where('warehouseId', '==', params.warehouseId));
      }
      
      if (params.type) {
        constraints.push(where('type', '==', params.type));
      }
      
      if (params.fromDate) {
        constraints.push(where('date', '>=', params.fromDate));
      }
      
      if (params.toDate) {
        constraints.push(where('date', '<=', params.toDate));
      }
      
      // Rendezés dátum szerint a megadott irány alapján (alapértelmezetten csökkenő)
      constraints.push(firestoreOrderBy('date', params.sortOrder || 'desc'));
      
      // Lapozás - vagy limit vagy pageSize használata
      const pageSize = params.pageSize || 100; // Alapértelmezett oldál méret
      constraints.push(firestoreLimit(params.limit || pageSize));
      
      // Ha van lastDoc, akkor attól kezdve kérjük le az adatokat (lapozás)
      let q = query(movementsRef, ...constraints);
      
      // Adatok lekérése
      const snapshot = await getDocs(q);
      
      const movementsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      })) as InventoryMovement[];
      
      // Utolsó dokumentum mentése lapozáshoz
      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
      
      // Ha első lap, akkor beállítjuk a teljes találatok számát
      // (Megjegyzés: Firestore-ban nincs egyszerű módja a pontos COUNT lekérdezésnek,
      // ezért ez csak egy közelítés)
      let totalCount = 0;
      if (!params.lastDoc) {
        // Ha van kevesebb elem mint a pageSize, akkor pontosan tudjuk a számot
        if (snapshot.size < pageSize) {
          totalCount = snapshot.size;
        } else {
          // Ha tele van az oldal, akkor becsüljük 3-5x-re a pageSize-t
          // Ez reálisabb becslés mint a 1000x
          totalCount = pageSize * 5; // Így 20 * 5 = 100 helyett 20,000
        }
      }
      
      setMovements(movementsList);
      setError(null);
      
      // Visszaadjuk a mozgásokat, az utolsó dokumentumot és a becsült teljes számot
      return {
        movements: movementsList,
        lastDoc: lastVisible,
        totalCount
      };
    } catch (err) {
      console.error('Hiba a készletmozgások betöltésekor:', err);
      setError('Nem sikerült betölteni a készletmozgásokat.');
      return {
        movements: [],
        lastDoc: null,
        totalCount: 0
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Készletmozgás létrehozása
  const createInventoryMovement = useCallback(async (data: CreateInventoryMovementData): Promise<InventoryMovement> => {
    if (!currentUser) {
      throw new Error('A felhasználó nincs bejelentkezve.');
    }
    
    // Tranzakció indítása
    return runTransaction(db, async (transaction) => {
      try {
        // ===== ELSŐ FÁZIS: MINDEN OLVASÁSI MŰVELET =====
        
        // Termék adatainak lekérése
        const productRef = doc(db, 'products', data.productId);
        const productDoc = await transaction.get(productRef);
        
        if (!productDoc.exists()) {
          throw new Error('A termék nem található.');
        }
        
        const productData = productDoc.data();
        
        // Raktár adatainak lekérése
        const warehouseRef = doc(db, 'warehouses', data.warehouseId);
        const warehouseDoc = await transaction.get(warehouseRef);
        
        if (!warehouseDoc.exists()) {
          throw new Error('A raktár nem található.');
        }
        
        // Felhasználó adatainak lekérése
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await transaction.get(userRef);
        
        // Forrásdokumentum lekérése (meglévő készlet az adott raktárban)
        const inventoryQuery = query(
          collection(db, 'inventory'), 
          where('productId', '==', data.productId),
          where('warehouseId', '==', data.warehouseId)
        );
        
        const inventoryDocs = await getDocs(inventoryQuery);
        
        let inventoryRef;
        let currentQuantity = 0;
        
        // Ha van már készlet, akkor azt frissítjük
        if (!inventoryDocs.empty) {
          const matchingDocs = data.locationId 
            ? inventoryDocs.docs.filter(doc => doc.data().locationId === data.locationId)
            : inventoryDocs.docs;
            
          if (matchingDocs.length > 0) {
            inventoryRef = doc(db, 'inventory', matchingDocs[0].id);
            currentQuantity = matchingDocs[0].data().quantity || 0;
          } else {
            inventoryRef = doc(collection(db, 'inventory'));
          }
        } else {
          inventoryRef = doc(collection(db, 'inventory'));
        }
        
        // Célraktár adatainak lekérése (csak átmozgatás esetén)
        let targetWarehouseDoc = null;
        let targetInventoryDocs = null;
        let targetInventoryRef = null;
        let targetQuantity = 0;
        
        if (data.type === InventoryMovementType.STOCK_TRANSFER && data.targetWarehouseId) {
          const targetWarehouseRef = doc(db, 'warehouses', data.targetWarehouseId);
          targetWarehouseDoc = await transaction.get(targetWarehouseRef);
          
          if (!targetWarehouseDoc.exists()) {
            throw new Error('A célraktár nem található.');
          }
          
          const targetInventoryQuery = query(
            collection(db, 'inventory'),
            where('productId', '==', data.productId),
            where('warehouseId', '==', data.targetWarehouseId)
          );
          
          targetInventoryDocs = await getDocs(targetInventoryQuery);
          
          if (!targetInventoryDocs.empty) {
            targetInventoryRef = doc(db, 'inventory', targetInventoryDocs.docs[0].id);
            targetQuantity = targetInventoryDocs.docs[0].data().quantity || 0;
          } else {
            targetInventoryRef = doc(collection(db, 'inventory'));
          }
        }
        
        // ===== MÁSODIK FÁZIS: SZÁMÍTÁSOK ÉS VALIDÁCIÓK =====
        
        // Felhasználó név összeállítása
        let userName = 'Ismeretlen felhasználó';
        let userEmail = currentUser.email || '';
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          if (userData?.displayName) {
            userName = userData.displayName;
          } else {
            const firstName = userData?.firstName || '';
            const lastName = userData?.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim();
            
            if (fullName) {
              userName = fullName;
            } else if (userData?.email) {
              userName = userData.email;
            } else if (currentUser.email) {
              userName = currentUser.email;
            }
          }
          
          if (userData?.email) {
            userEmail = userData.email;
          }
        } else if (currentUser.email) {
          userName = currentUser.email;
          userEmail = currentUser.email;
        }
        
        // Célraktárnév lekérése
        let targetWarehouseName = undefined;
        if (targetWarehouseDoc) {
          targetWarehouseName = targetWarehouseDoc.data()?.name;
        }
        
        // Új mennyiség kiszámítása a mozgás típusa alapján
        let newQuantity = currentQuantity;
        
        switch (data.type) {
          case InventoryMovementType.STOCK_IN:
            newQuantity = currentQuantity + data.quantity;
            break;
          case InventoryMovementType.STOCK_OUT:
            if (currentQuantity < data.quantity) {
              throw new Error('Nincs elegendő készlet a kiadáshoz.');
            }
            newQuantity = currentQuantity - data.quantity;
            break;
          case InventoryMovementType.STOCK_ADJUSTMENT:
            newQuantity = data.quantity;
            break;
          case InventoryMovementType.STOCK_INVENTORY:
            newQuantity = data.quantity;
            break;
          case InventoryMovementType.STOCK_TRANSFER:
            if (currentQuantity < data.quantity) {
              throw new Error('Nincs elegendő készlet az átmozgatáshoz.');
            }
            newQuantity = currentQuantity - data.quantity;
            
            if (!data.targetWarehouseId) {
              throw new Error('Átmozgatás esetén a célraktár megadása kötelező.');
            }
            break;
          case InventoryMovementType.STOCK_RESERVATION:
            if (currentQuantity < data.quantity) {
              throw new Error('Nincs elegendő készlet a foglaláshoz.');
            }
            break;
          case InventoryMovementType.STOCK_RESERVATION_RELEASE:
            const currentReservedQuantity = inventoryDocs.empty ? 0 : (inventoryDocs.docs[0].data().reservedQuantity || 0);
            if (currentReservedQuantity < data.quantity) {
              throw new Error('Nincs ennyi foglalt készlet.');
            }
            break;
        }
        
        // ===== HARMADIK FÁZIS: MINDEN ÍRÁSI MŰVELET =====
        
        // Debug logging for attachments
        console.log('Movement creation debug:', {
          hasAttachments: data.attachments && data.attachments.length > 0,
          attachmentsArray: data.attachments,
          attachmentsLength: data.attachments?.length || 0
        });
        
        // Készletmozgás dokumentum létrehozása
        const movementRef = doc(collection(db, 'inventoryMovements'));
        
        // Készletmozgás adatainak összeállítása
        const movementData: InventoryMovement = {
          id: movementRef.id,
          productId: data.productId,
          productCode: productData.code,
          productName: productData.name,
          warehouseId: data.warehouseId,
          warehouseName: warehouseDoc.data()?.name,
          ...(data.targetWarehouseId && { targetWarehouseId: data.targetWarehouseId }),
          ...(targetWarehouseName && { targetWarehouseName }),
          ...(data.locationId && { locationId: data.locationId }),
          quantity: data.quantity,
          unit: productData.unit,
          date: new Date(),
          type: data.type,
          ...(data.documentId && { documentId: data.documentId }),
          ...(data.documentType && { documentType: data.documentType }),
          ...(data.documentNumber && { documentNumber: data.documentNumber }),
          userId: currentUser.uid,
          userName,
          userEmail,
          ...(data.note && { note: data.note }),
          ...(data.lotNumber && { lotNumber: data.lotNumber }),
          ...(data.expiryDate && { expiryDate: data.expiryDate }),
          ...(data.serialNumber && { serialNumber: data.serialNumber }),
          ...(data.attachments && data.attachments.length > 0 && { attachments: data.attachments }),
          isActive: true,
          createdAt: new Date()
        };
        
        // Készletmozgás mentése
        transaction.set(movementRef, movementData);
        
        // Forrás készlet frissítése
        if (data.type !== InventoryMovementType.STOCK_RESERVATION && 
            data.type !== InventoryMovementType.STOCK_RESERVATION_RELEASE) {
          
          transaction.set(inventoryRef, {
            productId: data.productId,
            productCode: productData.code,
            productName: productData.name,
            warehouseId: data.warehouseId,
            warehouseName: warehouseDoc.data()?.name,
            ...(data.locationId && { locationId: data.locationId }),
            quantity: newQuantity,
            unit: productData.unit,
            lastUpdated: new Date(),
            updatedBy: currentUser.uid
          }, { merge: true });
        } else if (data.type === InventoryMovementType.STOCK_RESERVATION) {
          // Készletfoglalás
          const reservedQuantity = inventoryDocs.empty ? 0 : (inventoryDocs.docs[0].data().reservedQuantity || 0);
          transaction.set(inventoryRef, {
            reservedQuantity: reservedQuantity + data.quantity
          }, { merge: true });
        } else if (data.type === InventoryMovementType.STOCK_RESERVATION_RELEASE) {
          // Készletfoglalás feloldása
          const currentReservedQuantity = inventoryDocs.empty ? 0 : (inventoryDocs.docs[0].data().reservedQuantity || 0);
          transaction.set(inventoryRef, {
            reservedQuantity: currentReservedQuantity - data.quantity
          }, { merge: true });
        }
        
        // Célraktár készletének frissítése (csak átmozgatás esetén)
        if (data.type === InventoryMovementType.STOCK_TRANSFER && targetInventoryRef && targetWarehouseDoc) {
          transaction.set(targetInventoryRef, {
            productId: data.productId,
            productCode: productData.code,
            productName: productData.name,
            warehouseId: data.targetWarehouseId,
            warehouseName: targetWarehouseDoc.data()?.name,
            quantity: targetQuantity + data.quantity,
            unit: productData.unit,
            lastUpdated: new Date(),
            updatedBy: currentUser.uid
          }, { merge: true });
        }
          
          // Termék összes készletének frissítése
          await updateProductTotalStock(transaction, data.productId);
        
        console.log('userName debug:', {
          userDocExists: userDoc.exists(),
          userData: userDoc.exists() ? userDoc.data() : null,
          finalUserName: userName,
          finalUserEmail: userEmail,
          currentUserEmail: currentUser.email
        });
        
        return movementData;
      } catch (err) {
        console.error('Hiba a készletmozgás létrehozásakor:', err);
        throw err;
      }
    });
  }, [currentUser]);
  
  // Termék összes készletének frissítése
  const updateProductTotalStock = async (
    transaction: Transaction,
    productId: string
  ) => {
    try {
      // Lekérjük a termék összes készletét
      const inventoryQuery = query(
        collection(db, 'inventory'),
        where('productId', '==', productId)
      );
      
      const inventoryDocs = await getDocs(inventoryQuery);
      
      // Kiszámoljuk az összes készletet
      let totalStock = 0;
      inventoryDocs.forEach((doc) => {
        totalStock += doc.data().quantity || 0;
      });
      
      // Frissítjük a termék dokumentumot
      const productRef = doc(db, 'products', productId);
      transaction.update(productRef, { 
        currentStock: totalStock,
        updatedAt: new Date()
      });
      
      return totalStock;
    } catch (err) {
      console.error(`Hiba a termék (${productId}) készletének frissítésekor:`, err);
      return null;
    }
  };
  
  // Készletjelentés lekérése
  const getInventoryReport = useCallback(async (productIds?: string[]): Promise<InventoryReport[]> => {
    try {
      if (productIds && productIds.length > 0) {
        // FireStore in query korlátozott 10 elemre, ezért több lekérdezésre kell bontani
        const chunkSize = 10;
        const chunks = [];
        
        for (let i = 0; i < productIds.length; i += chunkSize) {
          chunks.push(productIds.slice(i, i + chunkSize));
        }
        
        const results: Inventory[] = [];
        
        for (const chunk of chunks) {
          const inventoryRef = collection(db, 'inventory');
          const q = query(inventoryRef, where('productId', 'in', chunk));
          const snapshot = await getDocs(q);
          const items = snapshot.docs.map((doc) => doc.data() as Inventory);
          results.push(...items);
        }
        
        return processInventoryReport(results);
      } else {
        const inventoryRef = collection(db, 'inventory');
        const snapshot = await getDocs(inventoryRef);
        const items = snapshot.docs.map((doc) => doc.data() as Inventory);
        return processInventoryReport(items);
      }
    } catch (err) {
      console.error('Hiba a készletjelentés lekérésekor:', err);
      throw new Error('Nem sikerült lekérni a készletjelentést.');
    }
  }, []);
  
  // Készletjelentés feldolgozása
  const processInventoryReport = (inventoryItems: Inventory[]): InventoryReport[] => {
    const productMap = new Map<string, InventoryReport>();
    
    // Csoportosítás termékek szerint
    inventoryItems.forEach(item => {
      if (!productMap.has(item.productId)) {
        productMap.set(item.productId, {
          productId: item.productId,
          productCode: item.productCode,
          productName: item.productName,
          totalQuantity: 0,
          reservedQuantity: 0,
          availableQuantity: 0,
          unit: item.unit,
          warehouseDetails: []
        });
      }
      
      const report = productMap.get(item.productId)!;
      const quantity = item.quantity || 0;
      const reservedQuantity = item.reservedQuantity || 0;
      const availableQuantity = Math.max(0, quantity - reservedQuantity);
      
      report.totalQuantity += quantity;
      report.reservedQuantity += reservedQuantity;
      report.availableQuantity += availableQuantity;
      
      report.warehouseDetails.push({
        warehouseId: item.warehouseId,
        warehouseName: item.warehouseName,
        quantity,
        reservedQuantity,
        availableQuantity
      });
    });
    
    return Array.from(productMap.values());
  };

  // Készletmozgás frissítése (főként dokumentumok hozzáadásához)
  const updateInventoryMovement = useCallback(async (
    movementId: string, 
    updates: Partial<InventoryMovement>
  ): Promise<void> => {
    if (!currentUser) {
      throw new Error('A felhasználó nincs bejelentkezve.');
    }
    
    try {
      const movementRef = doc(db, 'inventoryMovements', movementId);
      await updateDoc(movementRef, {
        ...updates,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });
      
      console.log('Készletmozgás sikeresen frissítve:', movementId);
    } catch (err) {
      console.error('Hiba a készletmozgás frissítésekor:', err);
      throw new Error('Nem sikerült frissíteni a készletmozgást.');
    }
  }, [currentUser]);

  // Dokumentum eltávolítása készletmozgásból
  const removeDocumentFromMovement = useCallback(async (
    movementId: string,
    documentUrl: string
  ): Promise<void> => {
    if (!currentUser) {
      throw new Error('A felhasználó nincs bejelentkezve.');
    }
    
    try {
      // Először lekérjük a jelenlegi mozgást
      const movementRef = doc(db, 'inventoryMovements', movementId);
      const movementDoc = await getDoc(movementRef);
      
      if (!movementDoc.exists()) {
        throw new Error('A készletmozgás nem található.');
      }
      
      const movementData = movementDoc.data() as InventoryMovement;
      const currentAttachments = movementData.attachments || [];
      
      // Eltávolítjuk a megadott URL-t
      const updatedAttachments = currentAttachments.filter(url => url !== documentUrl);
      
      // Frissítjük a dokumentumot
      await updateDoc(movementRef, {
        attachments: updatedAttachments,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });
      
      console.log('Dokumentum sikeresen eltávolítva:', documentUrl);
    } catch (err) {
      console.error('Hiba a dokumentum eltávolításakor:', err);
      throw new Error('Nem sikerült eltávolítani a dokumentumot.');
    }
  }, [currentUser]);

  return {
    inventory,
    movements,
    loading,
    error,
    fetchInventory,
    fetchMovements,
    createInventoryMovement,
    getInventoryReport,
    updateInventoryMovement,
    removeDocumentFromMovement
  };
}; 